import { APIRoutes } from 'constants/';
import {
  Commitment,
  CommitmentActionTypes,
  CommitmentCurrentState,
  CommitmentOption,
  OutcomeTypes,
  Profiler,
  SurveyHistoryItem,
} from 'types/';
import {
  abandonStudyAndNextActionPayload,
  CommitmentAnswerPayload,
  CommitmentFeedbackPayload,
  CommitmentInitiatePayload,
  CommitmentNextActionPayload,
  CommitmentResultPayload,
  CommitmentVerisoulFaceCheckPayload,
  CommitmentVerisoulFaceCheckResponse,
  pauseProfilerDecisionAndNextActionPayload,
  PostbackTestResponse,
  ProfilerQuestionPayload,
  ReportVideoWatchedPayload,
  SkipAnswerAndNextActionPayload,
  spinAndNextActionPayload,
  SupplierIframeSettingsPayload,
  SupplierLogInPayload,
  SupplierSignUpPayload,
  SupplierStatsPayload,
  SupplierStatsQueryParams,
  SupplierTransactionsPayload,
  SupplierTransactionsQueryParams,
  SupplierUrlsParams,
  SupplierUrlsPayload,
} from 'store/api/types/';
import { baseApi, OnboardingTags, SurveyTags, UserTags } from './baseApi';

export const surveysApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    surveyHistory: build.query<SurveyHistoryItem[], void>({
      query: () => ({
        url: APIRoutes.SurveysHistory,
        method: 'GET',
      }),
    }),
    supplierIframeSettings: build.query<SupplierIframeSettingsPayload, void>({
      query: () => ({
        url: APIRoutes.SupplierIframeSettings,
        method: 'GET',
      }),
    }),
    updateSupplierIframeSettings: build.mutation<
      SupplierIframeSettingsPayload,
      SupplierIframeSettingsPayload
    >({
      query: (body) => ({
        url: APIRoutes.SupplierIframeSettings,
        method: 'POST',
        body,
      }),
    }),
    commitmentOptions: build.query<CommitmentOption[], void>({
      query: () => ({
        url: APIRoutes.CommitmentOptions,
        method: 'GET',
      }),
      providesTags: [SurveyTags.Options],
      keepUnusedDataFor: 5,
    }),
    commitmentNextAction: build.mutation<
      Commitment<CommitmentActionTypes>,
      CommitmentNextActionPayload
    >({
      query: (body) => ({
        url: APIRoutes.CommitmentNext,
        method: 'POST',
        body,
      }),
      // transformResponse: (response: Commitment<CommitmentActionTypes>) => {
      //   // Modify the response here
      //   // For example, let's add a timestamp to the response
      //   const resp = {
      //     ...response,
      //     timestamp: new Date().toISOString(),
      //   };
      //   // pass the skippable flag from action to profiler
      //   if ('profiler' in resp.action.data) {
      //     resp.action.data.profiler.skippable = resp.action.data.skippable;
      //   }
      //   return resp;
      // },
      invalidatesTags: [UserTags.UserBasic, UserTags.UserFull],
    }),
    commitmentInitiate: build.mutation<
      Commitment<CommitmentActionTypes>,
      CommitmentInitiatePayload
    >({
      query: (body) => ({
        url: APIRoutes.CommitmentInitiate,
        method: 'POST',
        body,
      }),
      invalidatesTags: [OnboardingTags.UserState, SurveyTags.Options],
    }),
    commitmentSpecialInitiate: build.mutation<
      Commitment<CommitmentActionTypes>,
      CommitmentInitiatePayload
    >({
      query: (body) => ({
        url: APIRoutes.CommitmentInitiateSpecial,
        method: 'POST',
        body,
      }),
      invalidatesTags: [OnboardingTags.UserState],
    }),
    commitmentDummyData: build.query<void, void>({
      query: () => ({
        url: APIRoutes.TEMP_CommitmentDummyData,
        method: 'GET',
      }),
    }),
    transactionsOutcomeFilterOptions: build.query<OutcomeTypes, void>({
      query: () => ({
        url: APIRoutes.OutcomeFilterOptions,
        method: 'GET',
      }),
    }),
    commitmentResult: build.mutation<void, CommitmentResultPayload>({
      query: (body) => ({
        url: APIRoutes.CommitmentResult,
        method: 'POST',
        body,
      }),
    }),
    commitmentAnswer: build.mutation<
      Commitment<CommitmentActionTypes>,
      CommitmentAnswerPayload
    >({
      query: (body) => ({
        url: APIRoutes.CommitmentSubmitAnswer,
        method: 'POST',
        body,
      }),
    }),
    commitmentAnswerAndNextAction: build.mutation<
      Commitment<CommitmentActionTypes>,
      CommitmentResultPayload
    >({
      query: (body) => ({
        url: APIRoutes.CommitmentSubmitAnswerAndNextAction,
        method: 'POST',
        body,
      }),
    }),
    commitmentLoginFromOutcome: build.mutation<
      Commitment<CommitmentActionTypes>,
      CommitmentResultPayload
    >({
      query: (body) => ({
        url: APIRoutes.CommitmentLoginFromOutcome,
        method: 'POST',
        body,
      }),
    }),
    getProfilerQuestion: build.query<Profiler, ProfilerQuestionPayload | void>({
      query: (body) => ({
        url: `${APIRoutes.TEMP_Profiler_Data}${
          body?.id ? `?id=${body.id}` : ''
        }`,
        method: 'GET',
      }),
    }),
    supplierLogIn: build.mutation<
      Commitment<CommitmentActionTypes>,
      SupplierLogInPayload
    >({
      query: (body) => ({
        url: APIRoutes.SupplierLogIn,
        method: 'POST',
        body,
      }),
    }),
    supplierSignUp: build.mutation<
      Commitment<CommitmentActionTypes>,
      SupplierSignUpPayload
    >({
      query: (body) => ({
        url: APIRoutes.SupplierSignUp,
        method: 'POST',
        body,
      }),
    }),
    supplierStats: build.query<
      SupplierStatsPayload,
      SupplierStatsQueryParams | void
    >({
      query: (body) => {
        const {
          from,
          until,
          date_grouping,
          country_codes,
          country_grouping,
          page,
          page_size,
        } = body || {};

        const buildQueryParam = (
          param: string,
          value?: string | string[] | boolean | number
        ) => {
          if (typeof value === 'boolean') {
            return `${param}=${value}`;
          }
          return value
            ? `${param}=${Array.isArray(value) ? value.join(',') : value}`
            : '';
        };

        const params = [
          buildQueryParam('from', from),
          buildQueryParam('until', until),
          buildQueryParam('date_grouping', date_grouping),
          buildQueryParam('country_grouping', country_grouping),
          buildQueryParam('country_codes', country_codes),
          buildQueryParam('page', page),
          buildQueryParam('page_size', page_size),
        ]
          .filter(Boolean)
          .join('&');

        return {
          url: `${APIRoutes.SupplierStats}${params ? `?${params}` : ''}`,
          method: 'GET',
        };
      },
    }),

    supplierTransactions: build.query<
      SupplierTransactionsPayload,
      SupplierTransactionsQueryParams | void
    >({
      query: (body) => {
        const {
          from,
          until,
          country_codes,
          mid,
          pid,
          outcome,
          page,
          page_size,
        } = body || {};

        const buildQueryParam = (
          param: string,
          value?: string | string[] | number | number[]
        ) =>
          value
            ? `${param}=${Array.isArray(value) ? value.join(',') : value}`
            : '';

        const params = [
          buildQueryParam('from', from),
          buildQueryParam('until', until),
          buildQueryParam('country_codes', country_codes),
          buildQueryParam('mid', mid),
          buildQueryParam('pid', pid),
          buildQueryParam('outcome', outcome),
          buildQueryParam('page', page),
          buildQueryParam('page_size', page_size),
        ]
          .filter(Boolean)
          .join('&');

        return {
          url: `${APIRoutes.SupplierTransactions}${params ? `?${params}` : ''}`,
          method: 'GET',
        };
      },
    }),
    getSupplierUrls: build.query<SupplierUrlsParams, void>({
      query: () => ({
        url: APIRoutes.SupplierUrls,
        method: 'GET',
      }),
    }),
    updateSupplierUrls: build.mutation<void, SupplierUrlsPayload>({
      query: (body) => ({
        url: APIRoutes.SupplierUrls,
        method: 'PATCH',
        body,
      }),
    }),
    getSupplierSendDummyPostback: build.query<PostbackTestResponse, void>({
      query: () => ({
        url: APIRoutes.SupplierSupplierSendDummyPostback,
        method: 'GET',
      }),
    }),

    commitmentSkipAnswerAndNextAction: build.mutation<
      Commitment<CommitmentActionTypes>,
      SkipAnswerAndNextActionPayload
    >({
      query: (body) => ({
        url: APIRoutes.CommitmentSkipAnswerAndNextAction,
        method: 'POST',
        body,
      }),
    }),
    commitmentSpinNextAction: build.mutation<
      Commitment<CommitmentActionTypes>,
      spinAndNextActionPayload
    >({
      query: (body) => ({
        url: APIRoutes.CommitmentSpinAndNextAction,
        method: 'POST',
        body,
      }),
    }),
    pauseProfilerDecisionAction: build.mutation<
      Commitment<CommitmentActionTypes>,
      pauseProfilerDecisionAndNextActionPayload
    >({
      query: (body) => ({
        url: APIRoutes.CommitmentPauseProfilerDecisionAndNextAction,
        method: 'POST',
        body,
      }),
    }),
    abandonStudyAction: build.mutation<
      Commitment<CommitmentActionTypes>,
      abandonStudyAndNextActionPayload
    >({
      query: (body) => ({
        url: APIRoutes.CommitmentAbandonStudyAndNextAction,
        method: 'POST',
        body,
      }),
    }),
    commitmentCurrentState: build.query<CommitmentCurrentState, void>({
      query: () => ({
        url: APIRoutes.CommitmentCurrentState,
        method: 'GET',
      }),
    }),
    commitmentSubmitFeedback: build.mutation<void, CommitmentFeedbackPayload>({
      query: (body) => ({
        url: APIRoutes.CommitmentSubmitFeedback,
        method: 'POST',
        body,
      }),
    }),
    commitmentVerisoulFaceCheckOutcome: build.mutation<
      Commitment<CommitmentActionTypes>,
      CommitmentVerisoulFaceCheckPayload
    >({
      query: (body) => ({
        url: APIRoutes.CommitmentVerisoulFaceCheckOutcome,
        method: 'POST',
        body,
      }),
    }),
    commitmentVerisoulFaceCheck: build.query<
      CommitmentVerisoulFaceCheckResponse,
      void
    >({
      query: () => ({
        url: APIRoutes.CommitmentVerisoulFaceCheck,
        method: 'GET',
      }),
    }),
    // Get profiler localization data
    getProfilerLocalization: build.query<
      any,
      { profiler_id?: number | null; profiler_handle?: string }
    >({
      query: (payload) => ({
        url: APIRoutes.ProfilerLocalization,
        method: 'POST',
        body: payload,
      }),
    }),
    // Report video watch completion for tracking
    reportVideoWatched: build.mutation<void, ReportVideoWatchedPayload>({
      query: (body) => ({
        url: APIRoutes.ReportVideoWatched,
        method: 'POST',
        body,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useSurveyHistoryQuery,
  useCommitmentDummyDataQuery,
  useCommitmentInitiateMutation,
  useCommitmentSpecialInitiateMutation,
  useCommitmentNextActionMutation,
  useCommitmentOptionsQuery,
  useTransactionsOutcomeFilterOptionsQuery,
  useCommitmentResultMutation,
  useCommitmentAnswerMutation,
  useCommitmentAnswerAndNextActionMutation,
  useCommitmentLoginFromOutcomeMutation,
  useSupplierLogInMutation,
  useSupplierSignUpMutation,
  useSupplierStatsQuery,
  useSupplierTransactionsQuery,
  useLazyCommitmentDummyDataQuery,
  useLazyGetProfilerQuestionQuery,
  useGetSupplierUrlsQuery,
  useUpdateSupplierUrlsMutation,
  useCommitmentSkipAnswerAndNextActionMutation,
  useSupplierIframeSettingsQuery,
  useUpdateSupplierIframeSettingsMutation,
  useLazyGetSupplierSendDummyPostbackQuery,
  useCommitmentSpinNextActionMutation,
  usePauseProfilerDecisionActionMutation,
  useAbandonStudyActionMutation,
  useCommitmentCurrentStateQuery,
  useCommitmentSubmitFeedbackMutation,
  useCommitmentVerisoulFaceCheckOutcomeMutation,
  useCommitmentVerisoulFaceCheckQuery,
  useGetProfilerLocalizationQuery,
  useReportVideoWatchedMutation,
} = surveysApi;
